import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { usersApi } from '@/api/endpoints/users'
import { useUI } from '@/stores/ui'

interface Props {
  open: boolean
  onClose: () => void
  admin: { id: string; first_name?: string; last_name?: string; email: string; can_manage_admins?: boolean }
  onSaved?: (updated: Props['admin']) => void
}

export default function EditAdminModal({ open, onClose, admin, onSaved }: Props) {
  const addToast = useUI((s) => s.addToast)
  const [firstName, setFirstName] = useState(admin.first_name || '')
  const [lastName, setLastName] = useState(admin.last_name || '')
  const [email, setEmail] = useState(admin.email)
  const [canManage, setCanManage] = useState(!!admin.can_manage_admins)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    setFirstName(admin.first_name || '')
    setLastName(admin.last_name || '')
    setEmail(admin.email)
    setCanManage(!!admin.can_manage_admins)
  }, [admin])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      const updated = await usersApi.update(admin.id, {
        first_name: firstName,
        last_name: lastName,
        can_manage_admins: canManage,
      })
      addToast({ message: 'Admin updated', type: 'success' })
      onSaved?.({ ...admin, first_name: updated.first_name, last_name: updated.last_name, email: admin.email, can_manage_admins: canManage })
      onClose()
    } catch (error: any) {
      addToast({ message: error.response?.data?.detail || 'Failed to update admin', type: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 hover-raise"
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          >
            <h3 className="text-lg font-semibold mb-2">Edit Admin</h3>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input className="input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow" placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                <input className="input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow" placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
              </div>
              <div>
                <input type="email" className="input-field w-full bg-gray-100 text-gray-500 cursor-not-allowed" placeholder="Email" value={email} readOnly disabled />
                <p className="text-xs text-gray-400 mt-1">Email (login) can't be changed here.</p>
              </div>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={canManage} onChange={(e) => setCanManage(e.target.checked)} />
                <span>Designated admin (can manage admins)</span>
              </label>
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" className="btn-secondary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]" onClick={onClose} disabled={submitting}>Cancel</button>
                <button type="submit" className="btn-primary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-yellow-400/60" disabled={submitting}>{submitting ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}



